import { X, Check, MoreVertical, TrendingUp, Plus, GripVertical, SkipForward, RefreshCw, Trash2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useApp } from '@/context/app-context';
import { useNavigate } from 'react-router-dom';
import type { SetData } from '@/lib/types';

export function TrackWorkout() {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const session = state.trackingSession;

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [restTimer, setRestTimer] = useState(90);
  const [workoutTimer, setWorkoutTimer] = useState(0);
  const [showExerciseMenu, setShowExerciseMenu] = useState(false);

  // Get the workout being tracked
  const workout = session ? state.workouts[session.workoutId] : null;

  // Workout timer
  useEffect(() => {
    const interval = setInterval(() => {
      setWorkoutTimer((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rest timer
  useEffect(() => {
    if (isResting && restTimer > 0) {
      const interval = setInterval(() => {
        setRestTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (isResting && restTimer === 0) {
      setIsResting(false);
      setRestTimer(90);
    }
  }, [isResting, restTimer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!session || !workout) {
    return (
      <div className="p-4 text-center space-y-4">
        <p style={{ color: '#71717A' }}>No active workout</p>
        <button
          onClick={() => navigate('/workouts')}
          className="px-4 py-2 rounded-lg text-white font-semibold text-sm"
          style={{ backgroundColor: '#3F3F46' }}
        >
          Back
        </button>
      </div>
    );
  }

  const currentExercise = session.exercises[currentExerciseIndex];
  if (!currentExercise) {
    return (
      <div className="p-4 text-center space-y-4">
        <p style={{ color: '#71717A' }}>No exercises in session</p>
        <button
          onClick={() => navigate('/workouts')}
          className="px-4 py-2 rounded-lg text-white font-semibold text-sm"
          style={{ backgroundColor: '#3F3F46' }}
        >
          Back
        </button>
      </div>
    );
  }

  // Initialize sets for current exercise if not already done
  const exerciseSets: SetData[] = Array.isArray(currentExercise.sets)
    ? currentExercise.sets
    : Array.from({ length: currentExercise.sets as number }, () => ({
        reps: 0,
        weight: 0,
        rir: undefined,
        warmup: false,
      }));

  const completedSets = exerciseSets.filter((s) => s.done).length;

  // Find the current (first incomplete) set index
  const currentSetIndex = exerciseSets.findIndex((s) => !s.done);

  // Compute exercise status for the carousel dots
  const getExerciseStatus = (index: number) => {
    const exercise = session.exercises[index];
    const sets: SetData[] = Array.isArray(exercise.sets)
      ? exercise.sets
      : Array.from({ length: exercise.sets as number }, () => ({
          reps: 0,
          weight: 0,
          rir: undefined,
          warmup: false,
        }));
    const doneCount = sets.filter((s) => s.done).length;
    const totalSets = sets.length;

    if (doneCount === totalSets && totalSets > 0) return 'completed';
    if (doneCount > 0) return 'in-progress';
    return 'upcoming';
  };

  // Lookup previous session data for this exercise from history
  const previousSessionSets = useMemo(() => {
    if (!state.history || state.history.length === 0) return [];
    // Walk backwards through history to find the most recent entry containing this exercise
    for (let i = state.history.length - 1; i >= 0; i--) {
      const entry = state.history[i];
      const match = entry.exercises.find(
        (ex) => ex.exerciseId === currentExercise.exerciseId || ex.name === currentExercise.name
      );
      if (match) return match.sets;
    }
    return [];
  }, [state.history, currentExercise.exerciseId, currentExercise.name]);

  // Compute previous best from history
  const previousBest = useMemo(() => {
    if (previousSessionSets.length === 0) return null;
    let best: { weight: number; reps: number } | null = null;
    for (const set of previousSessionSets) {
      if (!best || set.weight > best.weight || (set.weight === best.weight && set.reps > best.reps)) {
        best = { weight: set.weight, reps: set.reps };
      }
    }
    return best;
  }, [previousSessionSets]);

  const handleSetChange = (setIndex: number, data: Partial<SetData>) => {
    const updatedSet: SetData = {
      reps: data.reps ?? exerciseSets[setIndex]?.reps ?? 0,
      weight: data.weight ?? exerciseSets[setIndex]?.weight ?? 0,
      rir: data.rir ?? exerciseSets[setIndex]?.rir,
      warmup: data.warmup ?? exerciseSets[setIndex]?.warmup ?? false,
    };

    dispatch({
      type: 'UPDATE_TRACKING_SET',
      exerciseIndex: currentExerciseIndex,
      setIndex,
      set: updatedSet,
    });
  };

  const handleCompleteSet = (setIndex: number) => {
    const set = exerciseSets[setIndex];
    if (set) {
      handleSetChange(setIndex, { ...set, done: true });
      setIsResting(true);
      setRestTimer(90);
    }
  };

  const handleAddSet = () => {
    dispatch({
      type: 'ADD_TRACKING_SET',
      exerciseIndex: currentExerciseIndex,
      set: { reps: 0, weight: 0, rir: undefined, warmup: false },
    });
  };

  const handleRemoveSet = (setIndex: number) => {
    dispatch({
      type: 'REMOVE_TRACKING_SET',
      exerciseIndex: currentExerciseIndex,
      setIndex,
    });
  };

  const handleFinish = () => {
    dispatch({ type: 'SAVE_WORKOUT' });
    navigate('/workouts');
  };

  const handleCancel = () => {
    navigate('/workouts');
  };

  const abbreviateExercise = (name: string): string => {
    const abbreviations: { [key: string]: string } = {
      'Bench Press': 'Bench',
      'Incline Dumbbell Press': 'Inc DB',
      'Overhead Press': 'OHP',
      Dumbbell: 'DB',
      Barbell: 'BB',
    };

    if (abbreviations[name]) return abbreviations[name];

    let abbr = name;
    Object.entries(abbreviations).forEach(([full, short]) => {
      abbr = abbr.replace(full, short);
    });

    return abbr.length > 12 ? abbr.substring(0, 12) : abbr;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '100%' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={handleCancel}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <X size={24} style={{ color: '#F0F0F0' }} />
        </button>

        <div className="flex-1 text-center">
          <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
            {workout.name}
          </div>
          <div style={{ color: '#71717A', fontSize: '13px' }}>{formatTime(workoutTimer)}</div>
        </div>

        <button
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <MoreVertical size={24} style={{ color: '#71717A' }} />
        </button>
      </div>

      {/* Horizontal Exercise Selector */}
      <div
        style={{
          backgroundColor: '#27272A',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Scrollable Exercise Chips Row */}
        <div
          className="flex gap-2 px-4 py-3"
          style={{
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <style>{`
            div.flex.gap-2.px-4.py-3::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {session.exercises.map((exercise, index) => {
            const status = getExerciseStatus(index);
            const isActive = index === currentExerciseIndex;

            // Status dot color
            let dotColor = '#71717A'; // gray for upcoming
            if (status === 'completed') dotColor = '#4ADE80'; // green for done
            if (status === 'in-progress' && !isActive) dotColor = '#FF6B35';
            if (isActive) dotColor = '#FF6B35'; // orange for active

            return (
              <button
                key={index}
                onClick={() => setCurrentExerciseIndex(index)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
                style={{
                  backgroundColor: '#3F3F46',
                  border: isActive ? '2px solid #FF6B35' : '2px solid transparent',
                  minHeight: '32px',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              >
                {/* Status Dot */}
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    flexShrink: 0,
                  }}
                />

                {/* Exercise Name */}
                <span
                  style={{
                    color: isActive ? '#F0F0F0' : '#A1A1AA',
                    fontSize: '12px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {abbreviateExercise(exercise.name)}
                </span>
              </button>
            );
          })}

          {/* Add Exercise Button */}
          <button
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
            style={{
              backgroundColor: 'transparent',
              border: '2px dashed #52525b',
              minHeight: '32px',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            <Plus size={12} style={{ color: '#71717A' }} />
            <span
              style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Add
            </span>
          </button>
        </div>

        {/* Exercise Menu - Below the row, only for active exercise */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <button
            onClick={() => setShowExerciseMenu(!showExerciseMenu)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{
              backgroundColor: '#3F3F46',
              color: '#A1A1AA',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
            }}
          >
            <MoreVertical size={12} />
            Options
          </button>

          {/* Exercise Menu Dropdown */}
          {showExerciseMenu && (
            <div
              className="rounded-lg overflow-hidden"
              style={{
                backgroundColor: '#3F3F46',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                gap: '1px',
              }}
            >
              <button
                className="px-3 py-1.5 flex items-center gap-1.5"
                style={{
                  color: '#F0F0F0',
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: '#3F3F46',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setShowExerciseMenu(false)}
              >
                <GripVertical size={12} />
                Reorder
              </button>
              <div style={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
              <button
                className="px-3 py-1.5 flex items-center gap-1.5"
                style={{
                  color: '#F0F0F0',
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: '#3F3F46',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setShowExerciseMenu(false)}
              >
                <SkipForward size={12} />
                Skip
              </button>
              <div style={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
              <button
                className="px-3 py-1.5 flex items-center gap-1.5"
                style={{
                  color: '#F0F0F0',
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: '#3F3F46',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setShowExerciseMenu(false)}
              >
                <RefreshCw size={12} />
                Swap
              </button>
              <div style={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
              <button
                className="px-3 py-1.5 flex items-center gap-1.5"
                style={{
                  color: '#EF4444',
                  fontSize: '11px',
                  fontWeight: 600,
                  backgroundColor: '#3F3F46',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setShowExerciseMenu(false)}
              >
                <Trash2 size={12} />
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rest Timer Overlay */}
      {isResting && (
        <div
          className="p-4"
          style={{
            backgroundColor: '#FF6B35',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div style={{ color: '#18181B', fontSize: '13px', fontWeight: 600 }}>REST TIMER</div>
              <div style={{ color: '#18181B', fontSize: '32px', fontWeight: 700 }}>
                {formatTime(restTimer)}
              </div>
            </div>
            <button
              onClick={() => setIsResting(false)}
              className="px-4 py-2 rounded-lg"
              style={{
                backgroundColor: '#18181B',
                color: '#FF6B35',
                fontSize: '14px',
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Skip Rest
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Current Exercise Title */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <div style={{ color: '#F0F0F0', fontSize: '20px', fontWeight: 700 }}>
              {currentExercise.name}
            </div>
          </div>
          <div style={{ color: '#71717A', fontSize: '13px' }}>
            Exercise {currentExerciseIndex + 1} of {session.exercises.length}
          </div>
        </div>

        {/* Target Info */}
        <div
          className="p-4 mb-4"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div style={{ color: '#71717A', fontSize: '12px', marginBottom: '4px' }}>TARGET</div>
              <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
                {exerciseSets.length} sets &times; {currentExercise.reps} reps
              </div>
            </div>
            {previousBest && (
              <div className="text-right">
                <div style={{ color: '#71717A', fontSize: '12px', marginBottom: '4px' }}>
                  PREVIOUS BEST
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp size={14} style={{ color: '#4ADE80' }} />
                  <span style={{ color: '#4ADE80', fontSize: '14px', fontWeight: 700 }}>
                    {previousBest.weight} {state.unitPreference} &times; {previousBest.reps}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active Set Input Card (current set highlighted) */}
        {currentSetIndex >= 0 && (
          <div
            className="p-5 mb-4"
            style={{
              backgroundColor: '#27272A',
              borderRadius: '16px',
              border: '2px solid #FF6B35',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <span
                style={{
                  color: '#FF6B35',
                  fontSize: '14px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                }}
              >
                SET {currentSetIndex + 1} OF {exerciseSets.length}
              </span>
              <span style={{ color: '#71717A', fontSize: '13px' }}>
                {completedSets}/{exerciseSets.length} complete
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Weight Input */}
              <div>
                <label
                  style={{ color: '#71717A', fontSize: '11px', display: 'block', marginBottom: '6px' }}
                >
                  WEIGHT ({state.unitPreference})
                </label>
                <input
                  type="number"
                  value={exerciseSets[currentSetIndex].weight || ''}
                  onChange={(e) =>
                    handleSetChange(currentSetIndex, {
                      weight: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full rounded-lg text-center"
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '18px',
                    fontWeight: 700,
                    padding: '12px 8px',
                    border: 'none',
                  }}
                />
              </div>

              {/* Reps Input */}
              <div>
                <label
                  style={{ color: '#71717A', fontSize: '11px', display: 'block', marginBottom: '6px' }}
                >
                  REPS
                </label>
                <input
                  type="number"
                  value={exerciseSets[currentSetIndex].reps || ''}
                  onChange={(e) =>
                    handleSetChange(currentSetIndex, {
                      reps: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full rounded-lg text-center"
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '18px',
                    fontWeight: 700,
                    padding: '12px 8px',
                    border: 'none',
                  }}
                />
              </div>
            </div>

            {/* RIR Selector */}
            <div className="mb-4">
              <label
                style={{ color: '#71717A', fontSize: '11px', display: 'block', marginBottom: '8px' }}
              >
                RIR (Reps in Reserve)
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3].map((rir) => (
                  <button
                    key={rir}
                    onClick={() => handleSetChange(currentSetIndex, { rir })}
                    className="rounded-lg"
                    style={{
                      backgroundColor:
                        exerciseSets[currentSetIndex].rir === rir ? '#FF6B35' : 'transparent',
                      color:
                        exerciseSets[currentSetIndex].rir === rir ? '#18181B' : '#71717A',
                      border: `2px solid ${exerciseSets[currentSetIndex].rir === rir ? '#FF6B35' : '#71717A'}`,
                      padding: '8px 4px',
                      fontSize: '12px',
                      fontWeight: 700,
                      minHeight: '44px',
                      cursor: 'pointer',
                    }}
                  >
                    {rir}
                  </button>
                ))}
              </div>
            </div>

            {/* Warmup Toggle */}
            <label className="flex items-center gap-2 text-xs mb-4" style={{ color: '#71717A' }}>
              <input
                type="checkbox"
                checked={exerciseSets[currentSetIndex].warmup || false}
                onChange={(e) =>
                  handleSetChange(currentSetIndex, { warmup: e.target.checked })
                }
                className="rounded"
              />
              <span>Warmup set</span>
            </label>

            <button
              onClick={() => handleCompleteSet(currentSetIndex)}
              className="w-full rounded-lg flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#FF6B35',
                color: '#18181B',
                padding: '14px',
                fontSize: '15px',
                fontWeight: 700,
                minHeight: '52px',
                cursor: 'pointer',
                border: 'none',
              }}
            >
              <Check size={20} />
              Complete Set
            </button>
          </div>
        )}

        {/* Set History - This Session */}
        <div className="mb-4">
          <div
            style={{
              color: '#71717A',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '8px',
              letterSpacing: '0.5px',
            }}
          >
            THIS SESSION
          </div>
          <div className="space-y-2">
            {exerciseSets.map((set, index) => (
              <div
                key={index}
                className="p-3 rounded-lg flex items-center justify-between"
                style={{
                  backgroundColor: set.done ? '#1F4428' : '#27272A',
                  border: `1px solid ${set.done ? '#4ADE80' : 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded"
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: set.done ? '#4ADE80' : '#3F3F46',
                      color: set.done ? '#18181B' : '#71717A',
                      fontSize: '13px',
                      fontWeight: 700,
                    }}
                  >
                    {set.done ? <Check size={16} /> : index + 1}
                  </div>
                  <div>
                    {set.done ? (
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>
                          {set.weight} {state.unitPreference} &times; {set.reps} reps
                        </span>
                        {set.warmup && (
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: '#71717A',
                              color: '#FFFFFF',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}
                          >
                            Warmup
                          </span>
                        )}
                        {set.rir !== undefined && (
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: '#FF6B35',
                              color: '#FFFFFF',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}
                          >
                            RIR {set.rir}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: '#71717A', fontSize: '14px' }}>
                        Set {index + 1} -{' '}
                        {index === currentSetIndex ? 'Current' : 'Not started'}
                      </div>
                    )}
                  </div>
                </div>
                {/* Remove set button for incomplete sets */}
                {!set.done && exerciseSets.length > 1 && (
                  <button
                    onClick={() => handleRemoveSet(index)}
                    style={{
                      color: '#EF4444',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add Set Button */}
          <button
            onClick={handleAddSet}
            className="w-full p-3 rounded-lg flex items-center justify-center gap-2 mt-3"
            style={{
              backgroundColor: 'transparent',
              border: '2px dashed #52525b',
              color: '#71717A',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '44px',
              cursor: 'pointer',
            }}
          >
            <Plus size={18} />
            Add Set
          </button>
        </div>

        {/* Previous Session */}
        {previousSessionSets.length > 0 && (
          <div className="mb-4">
            <div
              style={{
                color: '#71717A',
                fontSize: '12px',
                fontWeight: 600,
                marginBottom: '8px',
                letterSpacing: '0.5px',
              }}
            >
              PREVIOUS SESSION
            </div>
            <div
              className="p-4 rounded-lg"
              style={{
                backgroundColor: '#27272A',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div className="space-y-2">
                {previousSessionSets.map((set, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span style={{ color: '#71717A', fontSize: '13px' }}>Set {index + 1}</span>
                    <div className="flex items-center gap-2">
                      <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600 }}>
                        {set.weight} {state.unitPreference} &times; {set.reps}
                      </span>
                      {set.rir !== undefined && (
                        <span
                          className="px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: '#FF6B35',
                            color: '#FFFFFF',
                            fontSize: '10px',
                            fontWeight: 700,
                          }}
                        >
                          RIR {set.rir}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Next Exercise Preview */}
        {currentExerciseIndex < session.exercises.length - 1 && (
          <div
            className="p-4"
            style={{
              backgroundColor: '#27272A',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div
              style={{ color: '#71717A', fontSize: '11px', fontWeight: 600, marginBottom: '8px' }}
            >
              NEXT UP
            </div>
            <div style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 700 }}>
              {session.exercises[currentExerciseIndex + 1].name}
            </div>
            <div style={{ color: '#71717A', fontSize: '13px' }}>
              {session.exercises[currentExerciseIndex + 1].sets} sets &times;{' '}
              {session.exercises[currentExerciseIndex + 1].reps} reps
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#27272A',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={handleFinish}
          className="w-full rounded-xl flex items-center justify-center gap-2"
          style={{
            backgroundColor: '#4ADE80',
            color: '#18181B',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 700,
            minHeight: '56px',
            cursor: 'pointer',
            border: 'none',
          }}
        >
          <Check size={24} />
          Finish Workout
        </button>
      </div>
    </div>
  );
}
